'use client';

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet';
import {
  FaTimes, FaChevronLeft, FaChevronRight, FaImage,
  FaHome, FaIndustry, FaStar, FaBuilding,
  FaStore, FaShieldAlt, FaSchool, FaHospital, FaRoad, FaSearch
} from 'react-icons/fa';
import { useInView } from 'react-intersection-observer';
import Cta from '../components/NewCallToAction';

// Standardized Brand Colors
const BRAND_BLUE = "#3b82f6";
const DARK_SLATE = "#0f172a";

// --- DUMMY DATA ---
const DUMMY_PROJECTS = [
  {
    id: 'dummy-1',
    title: 'Lusail Iconic Tower',
    category: 'Commercial',
    description: 'A flagship high-rise development in the heart of Lusail City, showcasing advanced MEP services and luxury fit-out works.',
    details: 'This project involved the complete mobilization of 200+ skilled technicians for specialized electrical and HVAC installations.',
    images: [
      'https://images.unsplash.com/photo-1577495508048-b635879837f1?auto=format&fit=crop&w=800&q=80',
      'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=800&q=80'
    ],
    services: ['Electrical', 'HVAC', 'Fit-out'],
    duration: '24 Months',
    location: 'Lusail, Qatar'
  }
];
const Projects_data = [
  {
    id: 1,
    title: 'Doha Festival City',
    category: 'Commercial',
    description: 'Sub-contractor engagement with ROOTS QATAR WLL on the Doha Festival City project, supporting one of Qatar’s largest and most prestigious commercial developments.',
    details: 'As a sub-contractor under ROOTS QATAR WLL, our company provided skilled construction manpower across multiple trades, including civil works, electrical, plumbing, HVAC, fit-out, and finishing. Our team ensured timely mobilization, adherence to quality standards, and seamless coordination with the main contractor.',
    images: [
      'https://th.bing.com/th/id/R.8148b7c4dc4476b3fdb8487007428a69?rik=keAUY6wPZ7lLaQ&riu=http%3a%2f%2fmerraas.com%2fimages%2fprojects800x600%2fDFC.jpg&ehk=d%2fA653zHzHQCMQIyIbLb0hKu4ljnmVVDoZ%2b3xh5aICA%3d&risl=&pid=ImgRaw&r=0',
      'https://c.regencyholidays.com/blog/blog/content/images/2021/06/Doha-Festival-City-Mall.jpg',
      'https://training.ribccs.com/img/projects/Doha%20Festival%20City%20Mall.jpg'
    ],
    services: ['Civil', 'Electrical', 'Plumbing', 'HVAC', 'Fit-out', 'Finishing'],
    duration: 'Completed',
    location: 'Doha, Qatar'
  },
  {
    id: 2,
    title: 'Palms Mall',
    category: 'Commercial',
    description: 'Sub-contractor engagement with CONVERT ENGINEERING CO on the Palms Mall project, contributing to the development of a major retail and commercial complex in Qatar.',
    details: 'As a sub-contractor under CONVERT ENGINEERING CO, our company provided skilled construction manpower across multiple trades, including civil works, electrical, mechanical, HVAC, and retail fit-out. Our team ensured timely mobilization, adherence to quality standards, and coordination with the main contractor for seamless project execution.',
    images: [
      'https://palmsmall-qa.com/wp-content/uploads/2019/04/1-image0.jpg',
      'http://palmsmall-qa.com/wp-content/uploads/2019/04/231B9087.jpg',
      'https://d12eu00glpdtk2.cloudfront.net/public/images/palms-mall-shopping-malls-qatar.jpeg.jpg'
    ],
    services: ['Civil', 'Electrical', 'Mechanical', 'HVAC', 'Retail Fit-out'],
    duration: 'Completed',
    location: 'Doha, Qatar'
  },
  {
    id: 3,
    title: 'ISF Shooting Range',
    category: 'Government',
    description: 'Sub-contractor engagement with AL ALI ENGINEERING CO on the ISF Shooting Range project, supporting the construction of a specialized government facility in Qatar.',
    details: 'As a sub-contractor under AL ALI ENGINEERING CO, our company provided skilled manpower for MEP systems, safety installations, specialized equipment setup, civil works, and painting. Our team ensured precise execution, adherence to government standards, and full coordination with the main contractor.',
    images: [
      'https://training.ribccs.com/img/projects/Doha%20Festival%20City%20Mall.jpg'
    ],
    services: ['Civil', 'Electrical', 'Safety Systems', 'Specialized Equipment', 'Security', 'Painting'],
    duration: 'Completed',
    location: 'Qatar'
  },
  {
    id: 4,
    title: 'Foxhill Residential Building',
    category: 'Residential',
    description: 'Sub-contractor engagement with AL SEAL CONTRACTING & TRADING CO WLL on the Foxhill Residential Building project, supporting the construction of a luxury residential development in Doha.',
    details: 'As a sub-contractor under AL SEAL CONTRACTING & TRADING CO WLL, our company provided skilled manpower for electrical, plumbing, HVAC, painting, civil works, and luxury finishes. Our team ensured high-quality execution, attention to luxury finishes, and coordination with the main contractor.',
    images: [
      'https://aeb-qatar.com/wp-content/uploads/2016/06/Residential-Foxhills-02.jpg',
      'https://aeb-qatar.com/wp-content/uploads/2016/06/Residential-Foxhills-01.jpg',
      'https://static.wixstatic.com/media/0cd8cf_06e9d36029634049b3718cb72d2cb45e~mv2.jpg/v1/fit/w_736,h_508,q_90,enc_avif,quality_auto/0cd8cf_06e9d36029634049b3718cb72d2cb45e~mv2.jpg'
    ],
    services: ['Civil', 'Electrical', 'Plumbing', 'HVAC', 'Luxury Finishing', 'Painting'],
    duration: 'Completed',
    location: 'Doha, Qatar'
  },
  {
    id: 5,
    title: 'Adore Spa Salon',
    category: 'Commercial',
    description: 'Sub-contractor engagement with Al Balagh Trading Contracting on the Adore Spa Salon project, contributing to the decoration and fit-out of a high-end commercial facility in Qatar.',
    details: 'As a sub-contractor under Al Balagh Trading Contracting, our company provided skilled manpower for interior fit-out, electrical, plumbing, civil works, luxury finishing, and painting. Our team ensured premium quality execution, attention to detail, and coordination with the main contractor.',
    images: [
      'https://img1.wsimg.com/isteam/ip/e8f9ec0b-297b-48db-8240-afca3a55854e/DSC01598-0001.jpg',
      'https://img1.wsimg.com/isteam/ip/e8f9ec0b-297b-48db-8240-afca3a55854e/DSC01596-0001.jpg',
      'https://img1.wsimg.com/isteam/ip/e8f9ec0b-297b-48db-8240-afca3a55854e/DSC01598-0001.jpg'
    ],
    services: ['Civil', 'Interior Fit-out', 'Electrical', 'Plumbing', 'Luxury Finishing', 'Painting'],
    duration: 'Completed',
    location: 'Qatar'
  },
  {
    id: 6,
    title: 'Mesaieed Fire Station',
    category: 'Government',
    description: 'Sub-contractor engagement with Al Balagh Trading and Contracting Co. WLL on the Messaide Fire Station project, supporting the construction of a critical government infrastructure facility in Qatar.',
    details: 'As a sub-contractor under Al Balagh Trading and Contracting Co. WLL, our company provided skilled manpower for electrical works, safety systems, emergency equipment installation, HVAC, civil works, and painting. Our team ensured continuous workforce support, strict adherence to safety standards, and full coordination with the main contractor.',
    images: [
      'https://static.qm.org.qa/media/images/fire-station-building-doha-facad.39127ad0.fill-4520x2544.jpg'
    ],
    services: ['Civil', 'Electrical', 'Safety Systems', 'Emergency Equipment', 'HVAC', 'Painting'],
    duration: 'Completed',
    location: 'Messaide, Qatar'
  },
  {
    id: 7,
    title: 'AWQAF 94 Villas',
    category: 'Residential',
    description: 'Sub-contractor engagement with Al Balagh Trading and Contracting Co. WLL on the AWQAF 58 Villas project, supporting the construction of a large-scale residential development in Abu Hamour, Qatar.',
    details: 'As a sub-contractor under Al Balagh Trading and Contracting Co. WLL, our company provided skilled manpower for electrical, plumbing, HVAC, civil works, finishing, and landscaping across multiple villa units. Our team ensured timely deployment, quality workmanship, and full coordination with the main contractor.',
    images: [
      'https://almadar-holding.com/wp-content/uploads/2019/04/awqaf-s165.png',
      'https://sharjah24.ae/-/media/Sharjah24/Articles/2024/June-2024/25/00.jpg',
      'https://sharjah24.ae/-/media/Sharjah24/Articles/2023/October-2023/19/awq.jpg'
    ],
    services: ['Civil', 'Electrical', 'Plumbing', 'HVAC', 'Finishing', 'Landscaping'],
    duration: 'Completed',
    location: 'Abu Hamour, Qatar'
  },
  {
    id: 8,
    title: 'Twin Tower',
    category: 'Commercial',
    description: 'Sub-contractor engagement with ROOTS QATAR WLL on the Twin Tower project, supporting the construction of a prestigious commercial high-rise development in Doha, Qatar.',
    details: 'As a sub-contractor under ROOTS QATAR WLL, our company provided skilled manpower for electrical systems, HVAC, elevator installation, civil works, waterproofing, premium finishes, and painting. Our team ensured timely mobilization, adherence to quality standards, and seamless coordination with the main contractor.',
    images: [
      'https://www.alterego.co.rs/test/hamilton/images/projects/twin_tower_doha1.jpg',
      'https://www.alterego.co.rs/test/hamilton/images/projects/twin_tower_doha3.jpg',
      'https://www.alterego.co.rs/test/hamilton/images/projects/twin_tower_doha5.jpg'
    ],
    services: ['Civil', 'Electrical', 'HVAC', 'Elevator Systems', 'Premium Finishing', 'Painting', 'Waterproofing'],
    duration: 'Completed',
    location: 'Doha, Qatar'
  },
  {
    id: 9,
    title: 'Residential Tower Lusail City',
    category: 'Residential',
    description: 'Sub-contractor engagement with MAN ENTERPRISE QATAR WLL on the Residential Tower Lusail City project, supporting the construction of a modern residential tower in Lusail City, Qatar.',
    details: 'As a sub-contractor under MAN ENTERPRISE QATAR WLL, our company provided skilled manpower for electrical, plumbing, HVAC, smart home systems, civil works, protective coatings, luxury finishes, and painting. Our team ensured timely execution, adherence to quality standards, and close coordination with the main contractor.',
    images: [
      'https://res.cloudinary.com/dfdu54hxs/image/upload/v1768473626/Residential_Tower_Lussail_q3dcku.jpg',
    ],
    services: ['Civil', 'Electrical', 'Plumbing', 'HVAC', 'Smart Home Systems', 'Luxury Finishing', 'Painting', 'Protective Coatings'],
    duration: 'Completed',
    location: 'Lusail City, Qatar'
  },
  {
    id: 10,
    title: 'AL MADAR HOLDING',
    category: 'Commercial',
    description: 'Sub-contractor engagement on the AL MADAR HOLDING project, supporting the construction and fit-out of a commercial and corporate development in Qatar.',
    details: 'As a sub-contractor, our company provides skilled manpower for electrical systems, HVAC, building management systems, office fit-out, civil works, epoxy flooring, and painting. Our team ensures high-quality execution, coordination with the main contractor, and adherence to project timelines and specifications.',
    images: [
      'https://www.instagram.com/p/C6gtGglOkAH/media/?size=l'
    ],
    services: ['Civil', 'Electrical', 'HVAC', 'Building Management', 'Office Fit-out', 'Painting'],
    duration: 'Ongoing',
    location: 'Qatar'
  },
  {
    id: 11,
    title: 'CNG Gas Plant Station',
    category: 'Industrial',
    description: 'Sub-contractor engagement with BLACK CAT ENGINEERING & CONTRACTION WLL on the CNG Gas Plant Station project, supporting the construction of a specialized industrial facility in Qatar.',
    details: 'As a sub-contractor under BLACK CAT ENGINEERING & CONTRACTION WLL, our company provided skilled manpower for industrial electrical systems, safety systems, gas equipment installation, HVAC, civil works, industrial waterproofing, and painting. Our team ensured strict adherence to safety protocols, high-quality execution, and close coordination with the main contractor.',
    images: [
      'https://res.cloudinary.com/dfdu54hxs/image/upload/v1768633676/image_7_wchvgf.png'
    ],
    services: ['Civil', 'Industrial Electrical', 'Safety Systems', 'Gas Equipment', 'Industrial HVAC', 'Painting', 'Waterproofing'],
    duration: 'Completed',
    location: 'Qatar'
  },
  {
    id: 12,
    title: 'VOLTAGE ENGINEERING CO',
    category: 'Industrial',
    description: 'Sub-contractor engagement on projects with VOLTAGE ENGINEERING CO, supporting specialized industrial electrical and engineering works in Qatar.',
    details: 'As a sub-contractor, our company provides skilled manpower for high-voltage electrical systems, industrial automation, engineering works, civil works, and painting. Our team ensures precise execution, adherence to industrial standards, and coordination with the main contractor.',
    images: [
      'https://res.cloudinary.com/dfdu54hxs/image/upload/v1768473791/Voltage_Engineering_fqh2cm.jpg',
      'https://res.cloudinary.com/dfdu54hxs/image/upload/v1768473793/Voltage_owvftq.jpg',
    ],
    services: ['Civil', 'High Voltage Electrical', 'Industrial Automation', 'Engineering', 'Industrial Systems', 'Painting'],
    duration: 'Completed',
    location: 'Qatar'
  },
  {
    id: 13,
    title: 'Ain Khalid Gate',
    category: 'Commercial',
    description: 'Sub-contractor engagement with HASSANESCO QATAR on the Ain Khalid Gate project, supporting the construction of a commercial development in Ain Khalid, Qatar.',
    details: 'As a sub-contractor under HASSANESCO QATAR, our company provided skilled manpower for commercial fit-out, retail systems, civil works, and painting. Our team ensured timely execution, high-quality workmanship, and seamless coordination with the main contractor.',
    images: [
      'https://i.pinimg.com/236x/f4/94/7f/f4947f48a90f0bb419fa4bbcd1c173ea.jpg'
    ],
    services: ['Civil', 'Commercial Fit-out', 'Retail Systems', 'Painting'],
    duration: 'Completed',
    location: 'Ain Khalid, Qatar'
  },
  {
    id: 14,
    title: 'Qatar Foundation Project',
    category: 'Education',
    description: 'Sub-contractor engagement with AIN KHALID GATE ATLANTA INTERIOR DESIGN CO on the Qatar Foundation project, supporting the construction of educational and research facilities in Qatar.',
    details: 'As a sub-contractor, our company provided skilled manpower for electrical systems, HVAC, civil works, painting, educational systems, and technology integration. Our team ensured high-quality execution, coordination with the main contractor, and adherence to project timelines and standards.',
    images: [
      'https://i.pinimg.com/236x/d6/1c/f8/d61cf857e73f2e7dc00cca1af847aee1.jpg',
    ],
    services: ['Civil', 'Electrical', 'HVAC', 'Educational Systems', 'Technology Integration', 'Painting'],
    duration: 'Completed',
    location: 'Qatar'
  },
  {
    id: 15,
    title: 'Jery Al Samur Logistic Park',
    category: 'Industrial',
    location: 'Qatar',
    duration: 'Ongoing',
    images: [
      'https://aeb-qatar.com/wp-content/uploads/2024/08/Jery-Al-Samour-Logistics-Park-03.jpg',
      'https://aeb-qatar.com/wp-content/uploads/2024/08/Jery-Al-Samour-Logistics-Park-01.jpg',
      'https://aeb-qatar.com/wp-content/uploads/2024/08/Jery-Al-Samour-Logistics-Park-02.jpg'
    ],
    services: ['Civil', 'Painting', 'Electrical Infrastructure', 'Industrial Systems', 'Logistics Facilities'],
    description: 'Sub-contractor engagement with MEKKDAM TECHNOLOGY on the Jery Al Samur Logistic Park project, supporting large-scale logistics and industrial park development with civil works and infrastructure installation.'
  },
  {
    id: 16,
    title: 'Doha Oasis Electric Works',
    category: 'Commercial',
    location: 'Doha, Qatar',
    duration: 'Ongoing',
    images: [
      'https://rcalmana.com/wp-content/uploads/2020/09/BTDLCM_JA_0121_PROPERTY_EXTERIOR-1295-min.jpg',
      'https://rcalmana.com/wp-content/uploads/2020/09/Doha-Oasis-04-1920x1244.jpg',
      'https://rcalmana.com/wp-content/uploads/2020/09/QO-03-1920x1280.jpg'
    ],
    services: ['Civil', 'Painting', 'Electrical Infrastructure', 'Energy Systems', 'Building Services', 'Commercial Fit-out'],
    description: 'Sub-contractor engagement with ROOTS ENERGY AND SERVICES ENGINEERING on the Doha Oasis Electric Works project, providing skilled manpower for electrical systems, civil works, and painting for a commercial development.'
  },
  {
    id: 17,
    title: 'QP-FIFA Pipeline Project',
    category: 'Industrial',
    location: 'Mesaieed, Qatar',
    duration: 'Completed',
    images: [
      'https://www.oilandgasmiddleeast.com/cloud/2021/07/28/QatarProjects-1_0.jpg'
    ],
    services: ['Civil', 'Painting', 'Pipeline Construction', 'Industrial Electrical', 'Infrastructure'],
    description: 'Sub-contractor engagement with DOPET on the QP-FIFA Pipeline Project, supporting critical pipeline construction with specialized civil and electrical works.'
  },
  {
    id: 18,
    title: 'QP New Khuff Gas Well',
    category: 'Industrial',
    location: 'Dukhan, Qatar',
    duration: 'Completed',
    images: [
      'https://res.cloudinary.com/dfdu54hxs/image/upload/v1768473788/QP_New_Khuff_Gas_Well_t5qwyy.jpg',

    ],
    services: ['Civil', 'Painting', 'Gas Infrastructure', 'Industrial Electrical', 'Safety Systems'],
    description: 'Sub-contractor engagement with DOPET on the QP New Khuff Gas Well project, providing skilled manpower for civil engineering, industrial electrical systems, and safety compliance.'
  },
  {
    id: 19,
    title: 'QP MP Steam Boiler',
    category: 'Industrial',
    location: 'Mesaieed, Qatar',
    duration: 'Completed',
    images: [
      'https://www.blackcatqatar.com/wp-content/uploads/2020/05/IMG_20200109_095956-768x576.jpg',
      'https://www.blackcatqatar.com/wp-content/uploads/2020/05/IMG_20200102_092341-768x576.jpg',
      'https://metenders.com/project_cms/uploads/project/OryxProject2.jpg',
    ],
    services: ['Civil', 'Painting', 'Mechanical Systems', 'Industrial Electrical', 'Steam Systems', 'Safety Infrastructure'],
    description: 'Sub-contractor engagement on the QP MP Steam Boiler project, supporting industrial facility construction with mechanical systems, electrical works, civil works, and painting.'
  },
  {
    id: 20,
    title: 'DPFU Phase 1 Package-2',
    category: 'Industrial',
    location: 'Dukhan, Qatar',
    duration: 'Completed',
    images: [
      'https://www.blackcatqatar.com/wp-content/uploads/2020/03/IMG_20191205_101056-768x576.jpg',
      'https://www.blackcatqatar.com/wp-content/uploads/2020/03/IMG_20191205_091526-768x576.jpg'
    ],
    services: ['Civil', 'Painting', 'Sand Blasting', 'Industrial Painting', 'Pipeline Maintenance', 'Industrial Upgrades'],
    description: 'Sub-contractor engagement on the Dukhan Production Facility Upgrade (DPFU) Phase 1 Package-2, providing manpower for industrial maintenance, pipeline painting, sand blasting, and civil works.'
  },
  {
    id: 21,
    title: 'Military Medical Complex',
    category: 'Healthcare',
    location: 'Saliyah, Qatar',
    duration: 'Completed',
    images: [
      'https://res.cloudinary.com/dfdu54hxs/image/upload/v1768473785/Milatry_Medical_Complex_qegurj.jpg',

    ],
    services: ['Civil', 'Painting', 'Medical Systems', 'Security Infrastructure', 'Electrical', 'HVAC', 'Medical Gas'],
    description: 'Sub-contractor engagement with HBK on the Military Medical Complex project, providing skilled manpower for healthcare systems, civil works, painting, and MEP installation.'
  },
  {
    id: 22,
    title: 'FIFA Readiness Project 3A',
    category: 'Infrastructure',
    location: 'Al Thumama, Qatar',
    duration: 'Completed',
    images: [
      'https://res.cloudinary.com/dfdu54hxs/image/upload/v1768473782/Fifa_Readiness_Project_vg2ufj.jpg',
    ],
    services: ['Civil', 'Painting', 'Water Infrastructure', 'Surface Water Management', 'Ground Water Systems'],
    description: 'Sub-contractor engagement with HBK on the FIFA Readiness Project 3A, providing skilled manpower for water network systems, civil works, and painting for surface and ground water infrastructure.'
  },
  {
    id: 23,
    title: 'Umm Slal Mohammed Roads',
    category: 'Infrastructure',
    location: 'Umm Slal Mohammed, Qatar',
    duration: 'Completed',
    images: [
      'https://res.cloudinary.com/dfdu54hxs/image/upload/v1768473789/Umm_Sahlal_Mohammad_Road_mpzaoj.jpg'
    ],
    services: ['Civil', 'Painting', 'Road Construction', 'Pipeline Works', 'Infrastructure Development', 'Utility Systems'],
    description: 'Sub-contractor engagement with HBK on Umm Slal Mohammed Roads project, providing civil works, road construction, pipeline installation, and painting services for infrastructure development.'
  },
  {
    id: 24,
    title: 'Al Thumama Stadium',
    category: 'Sports',
    location: 'Al Thumama, Qatar',
    duration: 'Completed',
    images: [
      'https://res.cloudinary.com/dfdu54hxs/image/upload/v1768473794/Al_Thaumama_Stadium_zegrkz.jpg',
      'https://www.thorntontomasetti.com/sites/default/files/styles/paragraph_slideshow/public/al-thumama-22.jpg?itok=OwQ54gr7'
    ],
    services: ['Civil', 'Painting', 'Electrical Systems', 'HVAC', 'Sports Infrastructure', 'Premium Finishing', 'Stadium Systems'],
    description: 'Sub-contractor engagement on Al Thumama Stadium project, supporting FIFA World Cup stadium construction with civil works, electrical systems, HVAC, painting, and premium sports facility finishes.'
  },
  {
    id: 25,
    title: 'CEC Electrical Renovation at J & G Restaurant',
    category: 'Commercial',
    location: 'Qatar',
    duration: 'Completed',
    images: [
      'https://res.cloudinary.com/dfdu54hxs/image/upload/v1768473621/J_G_Restaurant_a5zvcw.jpg'
    ],
    services: ['Civil', 'Painting', 'Electrical Renovation', 'Installation', 'Safety Compliance'],
    description: 'Sub-contractor engagement with CONFIDENT ENGINEERING AND CONSTRUCTION W.L.L (CEC) for electrical renovation and installation at J & G Restaurant, including civil works and painting.'
  },
  {
    id: 26,
    title: 'Majlis for Mr. Mohamme Ayed Hamad Al-Qahtani',
    category: 'Residential',
    location: 'Sailiyah, Qatar',
    duration: 'Completed',
    images: [
      'https://res.cloudinary.com/dfdu54hxs/image/upload/v1768633982/image_8_gmbfzk.png'
    ],
    services: ['Civil', 'Painting', 'Design & Build', 'Architectural', 'Electrical', 'Interior Works'],
    description: 'Sub-contractor engagement for the design & build of a private majlis for Mr. Mohamme Ayed Hamad Al-Qahtani, providing civil works, painting, MEP, and interior finishes.'
  },
  {
    id: 27,
    title: 'Majlis for Mr. Khaled Hamad RA Al Marri',
    category: 'Residential',
    location: 'Al Wukair, Qatar',
    duration: 'Completed',
    images: [
      'https://res.cloudinary.com/dfdu54hxs/image/upload/v1768634092/WhatsApp_Image_2025-11-20_at_10.32.40_AM_upchpr.jpg'
    ],
    services: ['Civil', 'Painting', 'Design & Build', 'Construction', 'MEP', 'Finishing'],
    description: 'Sub-contractor engagement for the ongoing design & build of a private majlis for Mr. Khaled Hamad RA Al Marri, including civil works, painting, MEP, and finishing.'
  },
  {
    id: 28,
    title: 'Giardino Village Residential Building',
    category: 'Residential',
    location: 'Qatar',
    duration: 'Completed',
    images: [
      'https://res.cloudinary.com/dfdu54hxs/image/upload/v1768473784/Giardina_Village_aybzd9.jpg',
      'https://ap2realestate.global/wp-content/uploads/1-25.jpg'
    ],
    services: ['Civil', 'Painting', 'Residential Construction', 'Electrical', 'Mechanical', 'Luxury Finishing'],
    description: 'Sub-contractor engagement with RAMACO TRADING AND CONTRACTING CO. W.L.L. on the Giardino Village Residential Building, providing civil works, painting, MEP, and luxury finishing services.'
  },
  {
    id: 29,
    title: 'Army Camp Sayliyah',
    category: 'Government',
    location: 'Sayliyah, Qatar',
    duration: 'In Progress',
    images: [
      'https://res.cloudinary.com/dfdu54hxs/image/upload/v1768479416/Army_Camp_i7fin8.jpg', // representative military facility
      'https://res.cloudinary.com/dfdu54hxs/image/upload/v1768479415/Army_Camp_Sailiya_upbj4j.jpg'
    ],
    services: ['Civil', 'Painting', 'Electrical', 'HVAC', 'Security Infrastructure'],
    description: 'Sub-contractor engagement with Qatar Armed Forces on the Army Camp Sayliyah project, providing civil works, painting, MEP installation, and security infrastructure services for military facility development.'
  },
  {
    id: 30,
    title: 'Al Zubarah Commercial Project',
    category: 'Commercial',
    location: 'Al Zubarah, Qatar',
    duration: 'In Progress',
    images: [
      'https://res.cloudinary.com/dfdu54hxs/image/upload/v1768479413/Al_Zubara_Project_mzlrui.jpg',
      'https://res.cloudinary.com/dfdu54hxs/image/upload/v1768479412/UrbaCon-Trading-Contracting-Company-Qatar-2-2-1024x379_gpcvfb.jpg'
    ],
    services: ['Civil', 'Painting', 'Electrical', 'Plumbing', 'Finishing'],
    description: 'Sub-contractor engagement with Urbacon Trading & Contracting on the Al Zubarah commercial project, providing skilled manpower for civil works, painting, MEP installations, and high-quality finishing services.'
  },
  {
    id: 31,
    title: 'Ras Laffan LPG Bottling Plant',
    category: 'Industrial',
    location: 'Ras Laffan, Qatar',
    duration: 'In Progress',
    images: [
      'https://res.cloudinary.com/dfdu54hxs/image/upload/v1768479420/LPG_bottling_Tadmur_t5arty.jpg'
    ],
    services: ['Civil', 'Painting', 'Industrial Electrical', 'Pipeline Systems', 'Mechanical Installations'],
    description: 'Sub-contractor engagement with Tadmur Contracting W.L.L. on the Ras Laffan LPG Bottling Plant, providing civil works, painting, industrial electrical systems, mechanical installations, and pipeline support.'
  },
  {
    id: 32,
    title: 'Sayliyah Sub-Station & Kahramaa Building',
    category: 'Infrastructure',
    location: 'Sayliyah, Qatar',
    duration: 'In Progress',
    images: [
      'https://res.cloudinary.com/dfdu54hxs/image/upload/v1768479412/Sailiya_Sub-Station_Karhamma_building_j27o0q.jpg', // representative substation
    ],
    services: ['Civil', 'Painting', 'Electrical Systems', 'HVAC', 'Sub-Station Works'],
    description: 'Sub-contractor engagement with Al Muftah Contracting Company for Sayliyah Sub-Station and Kahramaa Building, providing civil works, painting, electrical installations, and HVAC services for infrastructure development.'
  },
  {
    id: 33,
    title: 'Ras Laffan Industrial Project',
    category: 'Industrial',
    location: 'Ras Laffan, Qatar',
    duration: 'In Progress',
    images: [
      'https://res.cloudinary.com/dfdu54hxs/image/upload/v1768479415/Rotary_Contracting_Ras_Laffan_txp3uh.jpg', // industrial facility
    ],
    services: ['Civil', 'Painting', 'Mechanical Systems', 'Industrial Electrical', 'Pipeline Works'],
    description: 'Sub-contractor engagement with Rotary Contracting on the Ras Laffan industrial project, providing civil works, painting, industrial electrical systems, mechanical installations, and pipeline support.'
  },
  {
    id: 34,
    title: 'Ethylene Storage Plant',
    category: 'Industrial',
    location: 'Ras Laffan, Qatar',
    duration: 'In Progress',
    images: [
      'https://res.cloudinary.com/dfdu54hxs/image/upload/v1768479419/Ethynal_Project_ztoplw.jpg',

    ],
    services: [
      'Civil Works',
      'Formwork',
      'Reinforced Concrete',
      'Protective & Industrial Painting',
      'Structural Support Works'
    ],
    description: 'Sub-contractor engagement with Rotary Engineering Pte. Ltd. for the Ethylene Storage Plant at Ras Laffan. Scope includes civil works, heavy-duty formwork, reinforced concrete structures, and industrial protective painting in accordance with project specifications and safety standards.'
  }

];

const ProjectsPage = ({ projects = Projects_data }) => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

  const projectCategories = [
    { name: 'All', icon: <FaBuilding /> },
    { name: 'Commercial', icon: <FaStore /> },
    { name: 'Residential', icon: <FaHome /> },
    { name: 'Industrial', icon: <FaIndustry /> },
    { name: 'Government', icon: <FaShieldAlt /> },
    { name: 'Education', icon: <FaSchool /> },
    { name: 'Healthcare', icon: <FaHospital /> },
    { name: 'Infrastructure', icon: <FaRoad /> }
  ];

  const filteredProjects = projects.filter(project => {
    const matchesCategory = selectedCategory === 'All' || project.category === selectedCategory;
    const matchesSearch = project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.location.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const openProjectModal = (project) => {
    setSelectedProject(project);
    setCurrentImageIndex(0);
    document.body.style.overflow = 'hidden'; // Prevent scroll
  };

  const closeProjectModal = () => {
    setSelectedProject(null);
    document.body.style.overflow = 'unset';
  };

  return (
    <div className="projects-page" style={{ background: '#f8fafc', minHeight: '100vh', paddingBottom: '50px' }}>
      <Helmet>
        <title>
          Construction Projects & Portfolio Qatar | Abraj Al Dhafna
        </title>

        <meta
          name="description"
          content="Browse our construction portfolio in Qatar. Successful projects in civil works, MEP, fit-out, & manpower supply for Doha Festival City, Lusail Towers, and major government infrastructure."
        />

        <meta name="keywords" content="construction projects Qatar, civil engineering portfolio Doha, MEP projects Qatar, fit-out works portfolio, Abraj Al Dhafna projects, manpower supply case studies, construction achievements Qatar" />

        <link rel="canonical" href="https://abrajaldhafna.com/projects" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "WebSite",
                "@id": "https://abrajaldhafna.com/#website",
                "url": "https://abrajaldhafna.com/",
                "name": "Abraj Al Dhafna Trading & Contracting WLL",
                "publisher": { "@id": "https://abrajaldhafna.com/#organization" }
              },
              {
                "@type": "Organization",
                "@id": "https://abrajaldhafna.com/#organization",
                "name": "Abraj Al Dhafna Trading & Contracting WLL",
                "url": "https://abrajaldhafna.com/",
                "logo": "https://abrajaldhafna.com/logo.jpeg",
                "foundingDate": "2014",
                "telephone": "+97444170859",
                "address": {
                  "@type": "PostalAddress",
                  "addressLocality": "Doha",
                  "addressCountry": "QA"
                }
              },
              {
                "@type": "CollectionPage",
                "@id": "https://abrajaldhafna.com/projects#collection",
                "url": "https://abrajaldhafna.com/projects",
                "name": "Construction Projects in Qatar",
                "isPartOf": { "@id": "https://abrajaldhafna.com/#website" },
                "about": { "@id": "https://abrajaldhafna.com/#organization" }
              },
              {
                "@type": "ItemList",
                "@id": "https://abrajaldhafna.com/projects#itemlist",
                "name": "Construction & Manpower Supply Projects",
                "numberOfItems": projects.length,
                "itemListElement": projects.map((project, index) => ({
                  "@type": "ListItem",
                  "position": index + 1,
                  "item": {
                    "@type": "Project",
                    "name": project.title,
                    "description": project.description,
                    "location": project.location,
                    "category": project.category
                  }
                }))
              },
              {
                "@type": "BreadcrumbList",
                "@id": "https://abrajaldhafna.com/projects#breadcrumb",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://abrajaldhafna.com/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Projects",
                    "item": "https://abrajaldhafna.com/projects"
                  }
                ]
              }
            ]
          })}
        </script>
      </Helmet>
      <section aria-hidden="true" style={{ position: "absolute", left: "-9999px" }}>
        <h1>Construction Subcontracting & Manpower Supply Projects in Qatar</h1>
        <p>
          Abraj Al Dhafna Trading & Contracting WLL has successfully delivered
          construction subcontracting and skilled manpower supply projects across
          Qatar, including civil works, MEP services, industrial coatings, and
          large-scale labor deployment in Doha, Lusail, West Bay, and industrial zones.
        </p>
      </section>


      {/* --- HERO SECTION --- */}
      <section style={heroSectionStyles}>
        <div style={containerStyles}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            style={{ textAlign: 'center', padding: '0 20px' }}
          >
            <div style={badgeStyles}>
              <FaStar style={{ color: '#fbbf24', fontSize: '10px' }} />
              <span>PREMIER SUBCONTRACTOR QATAR</span>
            </div>
            <h1 style={heroTitleStyles}>
              Our <span style={{ color: '#fbbf24' }}>Milestone</span> Projects
            </h1>
            <p style={heroSubtitleStyles}>
              Delivering excellence through skilled manpower and certified tradesmen across Doha's most iconic developments.
            </p>
          </motion.div>
        </div>
      </section>

      {/* --- SEARCH & FILTERS --- */}
      <section style={{ marginTop: '-30px', position: 'relative', zIndex: 10, padding: '0 15px' }}>
        <div style={containerStyles}>
          {/* Search Bar */}
          <div style={searchContainerStyles}>
            <FaSearch style={{ color: '#94a3b8' }} />
            <input
              type="text"
              placeholder="Search by project name or location..."
              style={searchInputStyles}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Category Filters */}
          <div style={filterScrollWrapper}>
            <div style={filterGridStyles}>
              {projectCategories.map((category) => (
                <motion.button
                  key={category.name}
                  onClick={() => setSelectedCategory(category.name)}
                  style={{
                    ...filterBtnBase,
                    background: selectedCategory === category.name ? BRAND_BLUE : '#fff',
                    color: selectedCategory === category.name ? '#fff' : DARK_SLATE,
                    boxShadow: selectedCategory === category.name ? `0 10px 20px ${BRAND_BLUE}30` : '0 2px 8px rgba(0,0,0,0.05)',
                  }}
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {category.icon} {category.name}
                </motion.button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* --- PROJECTS GRID --- */}
      <section ref={ref} style={{ padding: '40px 15px' }}>
        <div style={containerStyles}>
          <div style={projectGridStyles}>
            {filteredProjects.length > 0 ? (
              filteredProjects.map((project) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  whileHover={{ y: -8 }}
                  onClick={() => openProjectModal(project)}
                  style={projectCardStyles}
                >
                  <div style={imageWrapperStyles}>
                    {project.images && project.images[0] ? (
                      <img src={project.images[0]} alt={project.title} style={imageStyles} loading="lazy" />
                    ) : (
                      <div style={placeholderStyles}><FaImage /></div>
                    )}
                    <div style={categoryBadgeStyles}>{project.category}</div>
                  </div>

                  <div style={cardContentStyles}>
                    <h3 style={cardTitleStyles}>{project.title}</h3>
                    <p style={cardDescStyles}>
                      {project.description.length > 90 ? project.description.substring(0, 90) + '...' : project.description}
                    </p>

                    <div style={metaRowStyles}>
                      <span style={metaItemStyles}><strong>Loc:</strong> {project.location}</span>
                      <span style={metaItemStyles}><strong>Dur:</strong> {project.duration}</span>
                    </div>
                  </div>
                  <div style={cardAccentLine} />
                </motion.div>
              ))
            ) : (
              <div style={noResultsStyles}>No projects found matching your criteria.</div>
            )}
          </div>
        </div>
      </section>

      {/* --- RESPONSIVE MODAL --- */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeProjectModal}
            style={modalOverlayStyles}
          >
            <motion.div
              initial={{ scale: 0.9, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 50 }}
              onClick={(e) => e.stopPropagation()}
              style={modalContentStyles}
            >
              <button onClick={closeProjectModal} style={closeBtnStyles}><FaTimes /></button>

              <div style={modalGalleryStyles}>
                <img
                  src={selectedProject.images[currentImageIndex]}
                  alt={selectedProject.title}
                  style={modalImgStyles}
                />
                {selectedProject.images.length > 1 && (
                  <>
                    <button onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(prev => (prev - 1 + selectedProject.images.length) % selectedProject.images.length) }} style={navBtnLeft}><FaChevronLeft /></button>
                    <button onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(prev => (prev + 1) % selectedProject.images.length) }} style={navBtnRight}><FaChevronRight /></button>
                    <div style={imgCounterStyles}>{currentImageIndex + 1} / {selectedProject.images.length}</div>
                  </>
                )}
              </div>

              <div style={modalTextContainer}>
                <div style={modalCategoryTag}>{selectedProject.category.toUpperCase()}</div>
                <h2 style={modalTitle}>{selectedProject.title}</h2>
                <p style={modalDescription}>{selectedProject.details || selectedProject.description}</p>

                <div style={modalMetaGrid}>
                  <div>
                    <span style={modalMetaLabel}>LOCATION</span>
                    <div style={modalMetaValue}>{selectedProject.location}</div>
                  </div>
                  <div>
                    <span style={modalMetaLabel}>DURATION</span>
                    <div style={modalMetaValue}>{selectedProject.duration}</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <Cta />
    </div>
  );
};

// --- RESPONSIVE STYLES ---
// --- CORRECTED RESPONSIVE STYLES ---

const containerStyles = {
  maxWidth: '1200px',
  margin: '0 auto',
  width: '100%',
};

const heroSectionStyles = {
  background: '#1e293b',
  padding: 'clamp(80px, 15vw, 140px) 0',
  color: '#fff',
};

const heroTitleStyles = {
  fontSize: 'clamp(1.8rem, 5vw, 3rem)',
  fontWeight: '800',
};

const responsiveGrid = {
  display: 'grid',
  // Fix: Use 1fr for mobile, and auto-fill for desktop
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  gap: '20px',
  width: '100%',
};

const projectCardStyles = {
  background: '#fff',
  borderRadius: '16px',
  overflow: 'hidden',
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  cursor: 'pointer',
  display: 'block', // Ensures it takes up space
  width: '100%',
};

const imageWrapperStyles = {
  height: '200px',
  width: '100%',
  position: 'relative',
};

const imageStyles = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
};

const filterScrollWrapper = {
  overflowX: 'auto',
  whiteSpace: 'nowrap',
  padding: '10px 0',
  scrollbarWidth: 'none', // Hide scrollbar for Firefox
  msOverflowStyle: 'none', // Hide scrollbar for IE/Edge
};

const filterGridStyles = {
  display: 'flex',
  gap: '10px',
  minWidth: 'max-content', // Essential for the horizontal scroll to work
};

const filterBtnBase = {
  padding: '8px 16px',
  borderRadius: '50px',
  border: '1px solid #e2e8f0',
  fontSize: '14px',
  fontWeight: '600',
  cursor: 'pointer',
};

const searchContainerStyles = {
  display: 'flex',
  alignItems: 'center',
  background: '#fff',
  padding: '0 15px',
  borderRadius: '12px',
  height: '50px',
  boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
  marginBottom: '15px'
};

const searchInputStyles = {
  border: 'none',
  outline: 'none',
  width: '100%',
  marginLeft: '10px',
  fontSize: '16px' // Prevents iOS zoom on focus
};

const cardContentStyles = { padding: '15px' };
const cardTitleStyles = { fontSize: '16px', fontWeight: '700', marginBottom: '5px', color: DARK_SLATE };
const cardDescStyles = { fontSize: '13px', color: '#64748b' };
const badgeStyles = { color: '#fbbf24', fontSize: '12px', fontWeight: '700', marginBottom: '10px' };
const categoryBadgeStyles = {
  position: 'absolute', top: '10px', left: '10px', background: '#fff',
  padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold'
};



const heroSubtitleStyles = {
  fontSize: 'clamp(0.95rem, 2vw, 1.15rem)',
  opacity: 0.8,
  maxWidth: '600px',
  margin: '0 auto',
  lineHeight: 1.6
};


const projectGridStyles = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 340px), 1fr))',
  gap: '25px'
};


const metaRowStyles = { display: 'flex', flexWrap: 'wrap', gap: '10px', borderTop: '1px solid #f1f5f9', paddingTop: '12px' };
const metaItemStyles = { fontSize: '11px', color: '#94a3b8' };
const cardAccentLine = { height: '4px', background: BRAND_BLUE, width: '100%' };

const modalOverlayStyles = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(15, 23, 42, 0.95)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '15px'
};

const modalContentStyles = {
  background: '#fff', width: '100%', maxWidth: '700px',
  borderRadius: '24px', overflowY: 'auto', maxHeight: '90vh', position: 'relative'
};

const modalGalleryStyles = { height: 'clamp(200px, 40vh, 350px)', position: 'relative', background: '#000' };
const modalImgStyles = { width: '100%', height: '100%', objectFit: 'cover' };
const modalTextContainer = { padding: '25px' };
const modalCategoryTag = { color: BRAND_BLUE, fontWeight: 800, fontSize: '11px', marginBottom: '8px' };
const modalTitle = { fontSize: '22px', fontWeight: 900, color: DARK_SLATE, marginBottom: '12px' };
const modalDescription = { color: '#64748b', lineHeight: 1.6, fontSize: '14px' };

const modalMetaGrid = {
  display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px',
  marginTop: '20px', padding: '15px', background: '#f8fafc', borderRadius: '12px'
};

const modalMetaLabel = { fontSize: '9px', fontWeight: 800, color: '#94a3b8', letterSpacing: '0.5px' };
const modalMetaValue = { fontSize: '13px', fontWeight: 700, color: DARK_SLATE };

const closeBtnStyles = {
  position: 'absolute', top: '15px', right: '15px', zIndex: 20,
  background: '#fff', border: 'none', width: '35px', height: '35px',
  borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
};

const navBtnBase = {
  position: 'absolute', top: '50%', transform: 'translateY(-50%)',
  background: 'rgba(255,255,255,0.3)', border: 'none', width: '35px', height: '35px',
  borderRadius: '50%', color: '#fff', cursor: 'pointer', backdropFilter: 'blur(4px)'
};
const navBtnLeft = { ...navBtnBase, left: '10px' };
const navBtnRight = { ...navBtnBase, right: '10px' };
const imgCounterStyles = {
  position: 'absolute', bottom: '15px', left: '50%', transform: 'translateX(-50%)',
  background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '3px 10px', borderRadius: '10px', fontSize: '11px'
};

const noResultsStyles = { textAlign: 'center', gridColumn: '1 / -1', padding: '50px', color: '#94a3b8' };

const placeholderStyles = {
  width: '100%', height: '100%', display: 'flex', alignItems: 'center',
  justifyContent: 'center', background: '#e2e8f0', color: '#94a3b8', fontSize: '30px'
};

export default ProjectsPage;